/**
 * FormField - Reusable form field wrapper with validation support
 * 
 * This component provides consistent validation UI across the application.
 * It wraps form inputs with label, error message, and helper text support.
 * 
 * @example
 * ```tsx
 * <FormField
 *   label="Email Address"
 *   required
 *   error={errors.email}
 *   helperText="We'll never share your email"
 * >
 *   <Input
 *     value={email}
 *     onChange={(e) => setEmail(e.target.value)}
 *     variant={errors.email ? 'error' : 'default'}
 *   />
 * </FormField>
 * ```
 */

import * as React from 'react';
import { cn } from '@/lib/utils';

export interface FormFieldProps {
  /** Field label */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Error message to display */
  error?: string;
  /** Helper text to display when there's no error */
  helperText?: string;
  /** Additional className for the container */
  className?: string;
  /** The input element to render */
  children: React.ReactNode;
}

export const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(
  ({ label, required, error, helperText, className, children }, ref) => {
    return (
      <div ref={ref} className={cn('space-y-2', className)}>
        {label && (
          <label className="block text-sm font-medium text-gray-700">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        
        {children}
        
        {error && (
          <p className="mt-1 text-sm text-red-500" role="alert">
            {error}
          </p>
        )}
        
        {!error && helperText && (
          <p className="mt-1 text-sm text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

FormField.displayName = 'FormField';
